import { useEffect, useMemo, useRef, useState } from 'react'
import { usePageTitle } from '../AppLayout'
import { api } from '../api/client'
import type { MarketEvent, MarketEventQuery } from '../api/client'
import { useHasPermission } from '../api/session'
import { useListParams } from '../hooks/useListParams'
import { useEndlessList } from '../hooks/useEndlessList'
import { useVerknuepft } from '../hooks/useVerknuepft'
import { Button } from '../components/common/Button'
import { Input, Select } from '../components/common/Controls'
import { EmptyState, ErrorBox, Loading } from '../components/common/Feedback'
import { FilterBar, FilterEnd, FilterFixed, FilterGrow }
  from '../components/common/FilterBar'
import { MultiSelect } from '../components/common/MultiSelect'
import type { MultiSelectOption } from '../components/common/MultiSelect'
import { PermissionNotice } from '../components/common/PermissionNotice'
import { MasterDetail } from '../components/layout/MasterDetail'
import { EreignisListe, gruppiereTage } from '../components/events/EreignisListe'
import { EreignisDetail } from '../components/events/EreignisDetail'
import { tagVersetzt } from '../components/events/ereignisZeit'
import s from './EventsPage.module.scss'
import { EVENT_TYPES, STATUS_TEXTE } from './eventVocabulary'

/** Das Recht, das diese Ansicht braucht. */
const PERMISSION = 'market_event:read'

const PAGE_SIZE = 50

/**
 * Wie viele Seiten hoechstens nachgeladen werden, um den heutigen Schnitt zu
 * erreichen. Bei fuenfzig Zeilen je Seite sind das dreihundert Termine —
 * mehr als eine zurueckliegende Woche je hat.
 */
const MAX_SEITEN_BIS_HEUTE = 6

/**
 * Das Zeitfenster, mit dem die Ansicht startet.
 *
 * EINE WOCHE ZURUECK, VIER WOCHEN VORAUS. Ereignisse sind Termine: Was
 * naechsten Monat ansteht, ist die Hauptfrage; was letzte Woche war, dient
 * der Einordnung. Der Rueckblick ist knapper als die Vorschau, weil ein
 * vergangener Termin sein Ergebnis schon getragen hat — ein kommender noch
 * nicht.
 */
const RUECKBLICK_TAGE = 7
const VORSCHAU_TAGE = 28

const TYP_OPTIONEN: MultiSelectOption[] = Object.entries(EVENT_TYPES)
  .map(([value, label]) => ({ value, label }))

//: Adresse ↔ Auswahl, wie auf den anderen Listen.
function alsListe(wert: string): string[] {
  return wert ? wert.split(',').filter(Boolean) : []
}

/**
 * Der Tag, an dem die Liste „hier stehen wir" markiert.
 *
 * HEUTE, wenn es dort Termine gibt — sonst der nächste Tag, an dem welche
 * liegen. Zurück wird nicht geschaut: Der nächste Termin ist die Frage, der
 * letzte steht ohnehin weiter oben. Nur aus den geladenen Zeilen.
 */
function bezugstag(zeilen: MarketEvent[]): string | null {
  const heute = tagVersetzt(0)
  const tage = zeilen
    .map((e) => e.date_from)
    .filter((d): d is string => Boolean(d))
  if (tage.includes(heute)) return heute
  const kommende = tage.filter((d) => d > heute).sort()
  return kommende[0] ?? null
}

/**
 * Die Ereignisse als Master–Detail.
 *
 * LINKS der Kalender, nach Tagen gruppiert, RECHTS das gewählte Ereignis mit
 * Zahlen, Belegen und Verknüpfungen — statt einer Tabelle mit dem Detail
 * darunter. Beim Laden springt die Liste zum heutigen Tag und wählt dessen
 * erstes Ereignis; Pfeiltasten und die Knöpfe über dem Detail blättern.
 *
 * Die Filter stehen in der Adresse; die Auswahl ist Zustand dieses
 * Augenblicks und steht nicht darin.
 */
export function EventsPage() {
  const allowed = useHasPermission(PERMISSION)
  const [gewaehlt, setGewaehlt] = useState<number | null>(null)

  // Das Fenster steht FEST, solange die Seite offen ist. Bei jedem Rendern
  // neu gerechnet wechselte es um Mitternacht mitten in der Sitzung — und
  // `touched` vergliche gegen ein bewegliches Ziel.
  const fenster = useMemo(() => ({
    von: tagVersetzt(-RUECKBLICK_TAGE),
    bis: tagVersetzt(VORSCHAU_TAGE),
  }), [])

  const list = useListParams({
    filters: { q: '', ref: '', type: '', status: '', von: '', bis: '' },
    // Wer weiter zurueck oder weiter voraus sehen will, aendert die Felder;
    // wer das Fenster ganz loswerden will, leert sie.
    preset: fenster,
    sort: 'date_from',
  })

  const params: MarketEventQuery = {
    q: list.filters.q.trim() || undefined,
    ref: list.filters.ref.trim() || undefined,
    event_type: alsListe(list.filters.type),
    status: alsListe(list.filters.status),
    // Leer heißt "kein Zeitraum" und fällt ganz weg.
    von: list.filters.von || undefined,
    bis: list.filters.bis || undefined,
    // ALS KALENDER, immer: Die Gruppen nach Tagen ergeben nur aufsteigend
    // einen Sinn.
    sort: 'date_from',
    dir: 'asc',
    size: PAGE_SIZE,
    total: true,
  }

  const { rows: zeilen, loading, loadingMore, error, denied, hasMore,
          loadMore, total }
    = useEndlessList(api.marketEvents, params, allowed)

  const heute = bezugstag(zeilen)
  const gruppen = useMemo(() => gruppiereTage(zeilen), [zeilen])
  const reihe = useMemo(() =>
    gruppen.flatMap((g) => g.eintraege.map((e) => e.id)), [gruppen])

  /**
   * Beim Laden so weit nachladen, bis der heutige Schnitt dabei ist.
   *
   * Das Zeitfenster beginnt sieben Tage in der Vergangenheit, und die Liste
   * kommt seitenweise; der heutige Tag kann auf Seite zwei liegen. Von selbst
   * laedt die Liste erst nach, wenn ihr Ende sichtbar wird — und das ist es
   * nicht, solange die Vergangenheit den Kasten fuellt. Begrenzt, damit ein
   * Fenster ohne Heute nicht endlos nachlaedt.
   */
  const nachgeladen = useRef(0)
  useEffect(() => {
    if (heute !== null || !hasMore || loading || loadingMore) return
    if (nachgeladen.current >= MAX_SEITEN_BIS_HEUTE) return
    nachgeladen.current += 1
    loadMore()
  }, [heute, hasMore, loading, loadingMore, loadMore])

  // Ohne Auswahl das erste Ereignis des heutigen Tags — dort steht man.
  // Verschwindet das gewählte durch einen Filterwechsel, rückt es nach.
  const vorgabe = gruppen.find((g) => g.tag !== null && g.tag === heute)
    ?.eintraege[0] ?? zeilen[0] ?? null
  const offen = zeilen.find((z) => z.id === gewaehlt) ?? vorgabe
  const index = offen ? reihe.indexOf(offen.id) : -1

  function springe(schritt: number) {
    if (reihe.length === 0) return
    const ziel = (index + schritt + reihe.length) % reihe.length
    setGewaehlt(reihe[ziel])
  }

  // Pfeiltasten blättern — außer in einem Eingabefeld, dort bewegen sie den
  // Cursor.
  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      const ziel = event.target as HTMLElement | null
      if (ziel && /INPUT|TEXTAREA|SELECT/.test(ziel.tagName)) return
      if (event.key === 'ArrowDown') { event.preventDefault(); springe(1) }
      if (event.key === 'ArrowUp') { event.preventDefault(); springe(-1) }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  })

  const { eintraege: verknuepft, laedt: verknuepftLaedt } = useVerknuepft(offen)

  const tage = gruppen.filter((g) => g.art === 'tag').length
  usePageTitle('Ereignisse', total !== null
    ? `${zeilen.length} von ${total} Ereignissen · ${tage} ${tage === 1 ? 'Tag' : 'Tage'}`
    : 'Termine und Veröffentlichungen')

  if (!allowed || denied) {
    return <PermissionNotice permission={PERMISSION}
                             subject="die Ereignisse" />
  }

  const position = (() => {
    if (!offen) return ''
    const gruppe = gruppen.find((g) => g.eintraege.some((e) => e.id === offen.id))
    if (gruppe) {
      const i = gruppe.eintraege.findIndex((e) => e.id === offen.id) + 1
      return `${gruppe.datum} · ${i} von ${gruppe.eintraege.length}`
    }
    return `${index + 1} von ${reihe.length}`
  })()

  return (
    <>
      <FilterBar>
        <FilterGrow>
          <Input label="Suche" placeholder="Titel, Bezeichnung oder Serie"
                 // OHNE autoFocus: Mit dem Cursor im Suchfeld bewegten die
                 // Pfeiltasten den Cursor statt der Auswahl.
                 value={list.filters.q}
                 onChange={(event) => list.setFilter('q', event.target.value)} />
        </FilterGrow>
        {/*
          Der Verweis MIT Präfix, so wie er gespeichert ist — die Schnittstelle
          kennt keine Suche nach dem Namen eines Ziels. Meist kommt er aus dem
          Popover eines Verweises, der Platzhalter zeigt die Form.
        */}
        <FilterFixed>
          <div className={s.bezug}>
            <Input label="Betrifft" placeholder="z. B. comm:BRENT"
                   value={list.filters.ref}
                   title="Der Verweis mit Präfix: isin:…, comm:…, ind:…, geo:…"
                   onChange={(event) => list.setFilter('ref', event.target.value)} />
          </div>
        </FilterFixed>
        <FilterFixed>
          <MultiSelect label="Art" options={TYP_OPTIONEN}
                       selected={alsListe(list.filters.type)}
                       onChange={(werte) => list.setFilter('type', werte.join(','))}
                       placeholder="alle Arten"
                       searchPlaceholder="Quartalszahlen, OPEC …" />
        </FilterFixed>
        {/*
          Zeitraum als ZWEI Felder und nicht als Vorauswahl („diese Woche",
          „nächster Monat"): Ein Kalender wird nach konkreten Tagen befragt.
          Beide Enden sind einschließend.
        */}
        <FilterFixed>
          <Input label="Von" type="date" value={list.filters.von}
                 title="Termine ab diesem Tag"
                 onChange={(event) => list.setFilter('von', event.target.value)} />
        </FilterFixed>
        <FilterFixed>
          <Input label="Bis" type="date" value={list.filters.bis}
                 title="Termine bis zu diesem Tag"
                 onChange={(event) => list.setFilter('bis', event.target.value)} />
        </FilterFixed>
        <FilterFixed>
          <Select label="Status" value={list.filters.status}
                  onChange={(event) => list.setFilter('status', event.target.value)}>
            <option value="">alle</option>
            {Object.entries(STATUS_TEXTE).map(([wert, text]) => (
              <option key={wert} value={wert}>{text}</option>
            ))}
          </Select>
        </FilterFixed>
        <FilterEnd>
          <Button onClick={list.reset} disabled={!list.touched}>
            Zurücksetzen
          </Button>
        </FilterEnd>
      </FilterBar>

      {error && <ErrorBox>{error}</ErrorBox>}

      <MasterDetail breite="minmax(360px, 36%)"
        liste={loading && zeilen.length === 0 ? (
          <Loading>lädt Ereignisse …</Loading>
        ) : (
          <EreignisListe items={zeilen} gewaehlt={offen?.id ?? null}
                         onWaehle={setGewaehlt} heute={heute}
                         onEndReached={loadMore} loadingMore={loadingMore}
                         leer={list.touched
                           ? 'Kein Ereignis passt zu diesen Filtern.'
                           : 'Es sind noch keine Ereignisse erfasst.'}
                         fuss={total !== null && zeilen.length < total
                           ? `${zeilen.length} von ${total} Ereignissen`
                           : `${zeilen.length} ${zeilen.length === 1 ? 'Ereignis' : 'Ereignisse'}`} />
        )}
        detail={offen ? (
          <EreignisDetail item={offen} position={position}
                          onVorherige={() => springe(-1)} onNaechste={() => springe(1)}
                          verknuepft={verknuepft} verknuepftLaedt={verknuepftLaedt} />
        ) : !loading && (
          <div className={s.ohne}>
            <EmptyState title="kein Ereignis gewählt">
              Links ein Ereignis wählen — oder die Filter lockern.
            </EmptyState>
          </div>
        )} />
    </>
  )
}
